/*globals login, showModalInfo, showModalConfirm, globalNavigate*/

import React, { useContext, useState } from "react";
import styled from "styled-components";
import Request from "common/Request";
import { openPolicy } from "views/Status/Subscribers/Report/ListSubscribers";
import ActionsContext from "common/ActionsContext";
import useProfile from "common/hooks/useProfile";
import BqnContext from "common/BqnContext";
import { aggregateIpAddresses } from "../api";
import Table from "common/graphs/Table/Table";
import { TextWithIcon, TimeCell } from "common/graphs/Table/TableRenders";
import Modal from "common/Modal";
import { atoiv6, atoiv4, durationToInt} from "common/utils";

const returnView = "viewSubscriberAttributes";

const TableContainer = styled.div`
  width: 100%;
  overflow: auto;
  padding-top: 26px;
`;

const handleWarnings = (response) => {
  const confirmedRE = /^Removed \d+ API subscriber/
  if (response.match(confirmedRE) !== null) {
    return
  }
  const notFoundRE = /^%WARN-ENOENT: Cannot find any "(.*)" API subscribers/;
  if (response.match(notFoundRE) !== null) {
    return;
  }
  showModalInfo("Warning:", response);
};

const confirmCreatePolicy = (policy) =>
  new Promise((resolve, reject) =>
    showModalConfirm(
      `Confirm before continue`,
      `Warning: This policy is undefined, and has been assigned to some subscribers or subscriber-groups. Those subscribers are now getting a default policy from the configured rules, while thos subscriber-groups are getting no rate limitation. If you click "Define", you can now define this policy and then the assigned subscribers and groups will get it.`,
      "warning",
      () => resolve(policy),
      () => reject(),
      "DEFINE",
      "RETURN"
    )
  );

const offerDefinePolicyIfNotFound = (name, settings = {}) =>
  confirmCreatePolicy(name)
    .then((policy) => {
      globalNavigate("viewPolicyRateEdit", {
        ...settings,
        returnView,
        create: policy,
      });
    })
    .catch(() => Promise.resolve());

const policyNotFoundRE = /%ERR-ENOENT: Cannot find "(.*)" policy/

const policyMayNotExist = outcome => {
  const notFoundMatch = outcome.match(policyNotFoundRE);
  if ( notFoundMatch === null ) {
    throw outcome;
  }
  const [_all, policyName] = notFoundMatch;
  return offerDefinePolicyIfNotFound(policyName)
}

const openOrCreatePolicy = (policyName, ...args) =>
  openPolicy(policyName, ...args).catch(policyMayNotExist);

const DeleteModalWrapper = styled.div`
  ul {
    padding: 0 0 0 15px;
    margin: -20px 0 20px 0;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    column-gap: 20px;

    li {
      margin: 3px 0 !important;
    }
  }
`;

const DeleteModal = ({
  showDeleteModal,
  setShowDeleteModal,
  items = [],
  whenDone,
}) => {
  const handleApply = () => {
    const cmds = items.map((ipAddress) => `clear api subscriber ${ipAddress}`);
    ifCl
      .run(`${cmds.join("\n")}\n`, /*is batch*/ true)
      .then(handleWarnings)
      .then(() => {
        setShowDeleteModal(false);
        whenDone();
      })
      .catch((error) => showModalError(error, ""));
  };
  if (!showDeleteModal) {
    return null;
  }
  const title =
    items.length === 1
      ? `Delete subscriber ${items[0]}?`
      : `Delete ${items.length} subscribers?`;
  return (
    <Modal
      title={title}
      onClose={() => setShowDeleteModal(false)}
      closeLabel="CANCEL"
      superIcon={"delete_forever"}
      large={items.length > 1 && items.length < 100}
      content={() => (
        <DeleteModalWrapper>
          {items.length === 1 ? (
            <p>You are removing any policy and subscriber-id assignments for <span className="col-blue">{items[0]}</span>, performed by RADIUS, the REST API or a billing integration, as well as all quotas. This will also remove <span className="col-blue">{items[0]}</span> from all subscriber groups. This will not remove <span className="col-blue">{items[0]}</span> from the BQN, just those assignments (if any). The <span className="col-blue">{items[0]}</span> subscriber will now be assigned to a new policy, according to the configured Subscriber Rate rules.</p>
          ) : null}
          {items.length > 1 ? (
            <>
              {items.length < 100 ? (
                <ul>
                  {items.map((name) => (
                    <li key={name}>{name}</li>
                  ))}
                </ul>
              ) : null}
              <p>
                You are removing any policy and subscriber-id assignments for the selected subscribers, performed by RADIUS, the REST API or a billing integration, as well as all quotas. This will also remove these subscribers from all subscriber groups. This will not remove the subscribers from the BQN, just those assignments (if any). The subscribers will now be assigned to new policies, according to the configured Subscriber Rate rules.
              </p>
            </>
          ) : null}
        </DeleteModalWrapper>
      )}
      applyLabel="YES"
      onApply={handleApply}
    />
  );
};

const openSubscriberDetails = ({ addr, subsId }) => {
  globalNavigate("viewSubsDetail", { returnView, addr, subsId });
};

const openSubscriberGroupsOn = (ipAddress) => {
  globalNavigate("viewStatusSubscriberGroups", { returnView, ipAddress });
};

const openSubscriberQuotas = (subnet) => {
  if (subnet !== "n/a") {
    globalNavigate("viewSubscriberQuotas", { returnView, subnet });
  }
};

const doesNothing = () => {};

const getColumns = ({
  profile = {},
  doReload = doesNothing,
  includeBqn,
  setDeleteSelected,
  setShowDeleteModal,
}) => {
  const { hideRates } = profile;
  return [
    ...(includeBqn
      ? [{
        label: "BQN",
        id: "BQN",
        numeric: true,
      }]
      : []
    ),
    {
      label: "IP-ADDRESS",
      id: "ADDRESS",
      cellClassName: "hyperlink-text",
      title: "Subscriber’s IP address",
      numeric: false,
      onClick: (addr) => openSubscriberDetails({ addr }),
      parse: (value) => {
        if(!value){
          return 0;
        }
        return value.includes(":") ? atoiv6(value) : atoiv4(value)
      },
      parseFilter: (value) => value
    },
    {
      label: "SUBSCRIBER-ID",
      id: "SUBSCRIBER-ID",
      numeric: false,
      cellClassName: (value) => (value !== "n/a" ? "hyperlink-text" : ""),
      onClick: (subsId) => {
        if (subsId === "n/a") {
          return;
        }
        openSubscriberDetails({ subsId })},
    },
    {
      label: "RATE-POLICY",
      id: "POLICY",
      numeric: false,
      onClick:(policyName, row) => {
        if (hideRates || policyName === "n/a") return;
        openOrCreatePolicy(
          policyName, null, includeBqn ? row["BQN"] : null
        )
      },
      renderCell: (value, row) => {
        const icon = row["UNDEFINED"] === "yes" ? "warning" : null;
        const className = `align-left${
      hideRates !== true && value !== "n/a" ? " hyperlink-text" : ""
    }${row["UNDEFINED"] === "yes" ? " col-red  cell-with-icon-left": ""
    }`;
        return <TextWithIcon icon={icon} value={value} className={className} />;
      },
    },
    {
      label: "ASSIGNED-BY",
      id: "P-ASSIGN",
      numeric: false,
    },
    {
      label: login.isPlatform("gs-kr") ? "BILLING-BLOCK" : "BLOCK",
      id: "BLOCK",
      numeric: false,
    },
    {
      label: "QUOTAS",
      id: "QUOTA",
      numeric: false,
      renderCell: (value, row) => {
        const className = value !== "n/a" ? "hyperlink-text" : "";
        return <TextWithIcon value={value} className={className} />;
      },
      onClick: (value, row) => {
        const subnet = row["ADDRESS"];
        openSubscriberQuotas(subnet);
      }
    },
    {
      label: "SUBSCRIBER-GROUPS",
      id: "SUBS-GROUPS",
      numeric: true,
      cellClassName: "align-right hyperlink-text",
      onClick: (value, row) => {
        const ipAddress = row["ADDRESS"];
        openSubscriberGroupsOn(ipAddress);
      }
    },
    {
      label: "UPDATE",
      id: "UPDATED",
      renderCell: (value) => <TimeCell value={value} />,
      numeric: true,
      parse: (data) => (data === "n/a" ? null : durationToInt(data)),
    },
    {
      label: "ACTIVE",
      id: "ACTIVE",
      numeric: false,
    },
    {
      label: "ACTIONS",
      id: "ACTIONS",
      actions: [
        {
          label: "Delete",
          icon: "delete",
          onClick: (row) => {
            setDeleteSelected([row["ADDRESS"]]);
            setShowDeleteModal(true);
          },
        },
      ],
    },
  ];
};

const ListSubscribers = ({ request, source }) => {
  const actions = useContext(ActionsContext);
  const { bqnId, bqns, clusterStatus } = useContext(BqnContext);
  const mode = clusterStatus?.mode;
  const profile = useProfile();
  const doReload = () => actions.send("do-load");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteSelected, setDeleteSelected] = useState([]);
  const [selected, setSelected] = useState([]);

  if (profile === null) {
    return null;
  };

  return (
    <>
      <TableContainer>
      <Request during={request}>
        {({ response }) => {
          if(mode === "cluster" && bqnId === "all"){
            response = aggregateIpAddresses(response, bqns.length);
          }

          const [ header, ...rows ] = response.trim().split("\n");
          const headerAttrs = header.trim().split(/\s+/);
          const newRows = rows.map((row) => {
            const rowAttrs = row.trim(/\s/).split(/\s+/);
            const newEntry = rowAttrs.reduce((acc, attr, index) => {
              acc[headerAttrs[index]] = attr;
              return acc;
            }, {});
            return newEntry;
          });
          const filteredRows = newRows.filter((row) => {
            const target = row["P-ASSIGN"];
            const bqn = row["BQN"] || null;

            return (source === "all" || target === source) &&
            (bqn === null || bqnId === "all" || bqn.toString() === bqnId.toString())
          })
          const filteredRowsWithIds = filteredRows.map((row, index) => ({
            ...row,
            id: row.id !== undefined ? row.id : index,
          }));

          return (
            <Table
              isSelectable={true}
              isRowSelectable={false}
              data={filteredRowsWithIds}
              sort={true}
              columns={getColumns({
                profile,
                doReload,
                includeBqn: mode === "cluster",
                setDeleteSelected,
                setShowDeleteModal,
              })}
              search={true}
              csvPrefix="subscriber-attributes"
              rowsPerPageNr={50}
              selected={selected}
              setSelected={setSelected}
              deleteSelected={(values) => {
                const selectedItems = filteredRowsWithIds
                  .filter((row) => values.includes(row.id))
                  .map((row) => row["ADDRESS"]);
                setDeleteSelected(selectedItems);
                setShowDeleteModal(true);
              }}
              />
          );
        }}
      </Request>
    </TableContainer>
    <DeleteModal
      showDeleteModal={showDeleteModal}
      setShowDeleteModal={setShowDeleteModal}
      items={deleteSelected}
      whenDone={() => {
        setSelected([]);
        doReload();
      }}
    />
  </>
  );
};

export default ListSubscribers;
